import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import {
  canSendViaWebchat,
  isWebchatBackground,
  isWebchatForeground,
} from "../../utils/webchatTransport";

const EXPANDED_PREF_KEY = "xpro.webchatTransportBar.expanded";

function readExpandedPref() {
  try {
    return sessionStorage.getItem(EXPANDED_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

function writeExpandedPref(expanded) {
  try {
    sessionStorage.setItem(EXPANDED_PREF_KEY, expanded ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export default function WebchatTransportBar({
  conversationId,
  presence,
  transport,
  onTransportChange,
  onPresenceRefresh,
}) {
  const { t } = useTranslation("chat");
  const [expanded, setExpanded] = useState(readExpandedPref);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const visible = isWebchatForeground(presence);
  const away = isWebchatBackground(presence);
  const allowWeb = canSendViaWebchat(presence);
  const viaWebchat = transport === "webchat";

  useEffect(() => {
    if (inviteError) setExpanded(true);
  }, [inviteError]);

  const setOpen = (next) => {
    setExpanded(next);
    writeExpandedPref(next);
  };

  const handleInvite = async () => {
    if (!conversationId || inviting) return;
    setInviting(true);
    setInviteError("");
    try {
      await fetchData(`/conversations/${conversationId}/webchat/invite`, "POST", {});
      if (typeof onPresenceRefresh === "function") onPresenceRefresh();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("webchatTransport.inviteFailed", "Não foi possível enviar o link.");
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const viaLabel = viaWebchat
    ? t("webchatTransport.viaWebchat", "Web Chat")
    : t("webchatTransport.viaWhatsapp", "WhatsApp");
  const collapsedLabel = t("webchatTransport.collapsedSummary", "Via {{channel}} · {{presence}}", {
    channel: viaLabel,
    presence: visible
      ? t("webchatTransport.openShort", "aberto")
      : away
        ? t("webchatTransport.awayShort", "em segundo plano")
        : t("webchatTransport.hiddenShort", "não visível"),
  });
  const toggleLabel = expanded
    ? t("webchatTransport.hideOptions", "Ocultar opções do Web Chat")
    : t("webchatTransport.showOptions", "Mostrar opções do Web Chat");

  return (
    <Box sx={{ width: "100%", mb: 0.75 }}>
      <Box
        onClick={() => !expanded && setOpen(true)}
        sx={(theme) => ({
          borderRadius: 2,
          border: "1px solid",
          borderColor: visible
            ? alpha(theme.palette.success.main, 0.45)
            : away
              ? alpha(theme.palette.info.main, 0.45)
              : theme.palette.divider,
          bgcolor: visible
            ? alpha(theme.palette.success.main, 0.06)
            : away
              ? alpha(theme.palette.info.main, 0.06)
              : theme.palette.background.paper,
          overflow: "hidden",
          cursor: expanded ? "default" : "pointer",
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minHeight: 30,
            px: 0.75,
            py: 0.15,
          }}
        >
          <Chip
            size="small"
            color={visible ? "success" : away ? "info" : "warning"}
            variant="outlined"
            label={collapsedLabel}
            sx={{
              fontSize: "0.72rem",
              height: 22,
              maxWidth: "100%",
              "& .MuiChip-label": {
                px: 0.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Tooltip title={toggleLabel}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!expanded);
              }}
              aria-expanded={expanded}
              aria-label={toggleLabel}
              sx={{ p: 0.25 }}
            >
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              px: 0.75,
              pb: 0.6,
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={transport}
              onChange={(_, next) => {
                if (!next) return;
                if (next === "webchat" && !allowWeb) return;
                onTransportChange(next);
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  py: 0,
                  px: 0.9,
                  height: 24,
                  fontSize: "0.72rem",
                },
              }}
            >
              <ToggleButton value="webchat" disabled={!allowWeb}>
                {t("webchatTransport.viaWebchat", "Web Chat")}
              </ToggleButton>
              <ToggleButton value="whatsapp">
                {t("webchatTransport.viaWhatsapp", "WhatsApp")}
              </ToggleButton>
            </ToggleButtonGroup>
            {!allowWeb ? (
              <Button
                size="small"
                variant="text"
                onClick={handleInvite}
                disabled={inviting}
                startIcon={<LinkOutlined sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.72rem",
                  minWidth: 0,
                  px: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {inviting
                  ? t("webchatTransport.inviting", "Enviando…")
                  : t("webchatTransport.sendLink", "Enviar link")}
              </Button>
            ) : null}
            {!allowWeb ? (
              <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 220px", minWidth: 0 }}>
                {t(
                  "webchatTransport.hiddenHint",
                  "O cliente não está com o chat aberto. A resposta vai pelo WhatsApp, ou envie o link para ele voltar."
                )}
              </Typography>
            ) : away ? (
              <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 220px", minWidth: 0 }}>
                {t(
                  "webchatTransport.awayHint",
                  "O cliente está com o chat aberto em segundo plano. A resposta chega no Web Chat."
                )}
              </Typography>
            ) : null}
          </Box>
        </Collapse>
      </Box>
      {inviteError ? (
        <Alert severity="warning" sx={{ mt: 0.75, py: 0 }} onClose={() => setInviteError("")}>
          {inviteError}
        </Alert>
      ) : null}
    </Box>
  );
}
